import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./SceneComposer.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

/* --- ImageModal --- */
function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cs-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="cs-modal__image" />
        <span className="cs-modal__label">{label}</span>
      </div>
    </div>
  );
}

/* --- Library picker modal --- */
function LibraryPicker({ onSelect, onClose }) {
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/images?page=${p}&per_page=12`, { headers });
      const d = await res.json();
      setImages(d.items || []);
      setPages(d.pages || 1);
      setPage(d.page || 1);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(1); }, [load]);

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="sc-library-picker" onClick={(e) => e.stopPropagation()}>
        <div className="sc-library-picker__header">
          <h3>Importar desde Biblioteca</h3>
          <button className="cs-modal__close" onClick={onClose}>&times;</button>
        </div>
        {loading && <div className="sc-library-picker__loading">Cargando...</div>}
        <div className="sc-library-picker__grid">
          {images.map((img) => (
            <div
              key={img.id}
              className="sc-library-picker__item"
              onClick={() => onSelect(img)}
            >
              <img
                src={img.png_path || `${API_URL}/images/${encodeURIComponent(img.id)}/file`}
                alt={img.id}
              />
              <span className="sc-library-picker__id">{img.id}</span>
            </div>
          ))}
        </div>
        <div className="sc-library-picker__pagination">
          <button disabled={page <= 1} onClick={() => load(page - 1)}>&lsaquo;</button>
          <span>{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => load(page + 1)}>&rsaquo;</button>
        </div>
      </div>
    </div>
  );
}

/* --- Main page --- */
export default function SceneComposer() {
  // Backgrounds
  const [backgrounds, setBgs] = useState([]);
  const [selectedBg, setSelectedBg] = useState(null); // path
  const [variations, setVariations] = useState([]);

  // Upload
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Library picker
  const [showLibrary, setShowLibrary] = useState(false);
  const [importing, setImporting] = useState(false);

  // Generation
  const [status, setStatus] = useState("idle");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);
  const pollingRef = useRef(false);

  // Reel refs
  const bgsReelRef = useRef(null);
  const varsReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  const headers = { Authorization: `Bearer ${getToken()}` };

  const filterPlaceholders = (items) =>
    (items || []).filter((i) => !i.name.includes(".emptyFolderPlaceholder"));

  // Load scenes on mount
  const loadScenes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/scenes`, { headers });
      const d = await res.json();
      setBgs(filterPlaceholders(d.backgrounds || []));
      setVariations(filterPlaceholders(d.variations || []));
    } catch {}
  }, []);

  useEffect(() => { loadScenes(); }, [loadScenes]);

  // --- Resize image to fit within Vercel 4.5MB body limit ---
  const resizeImage = (file, maxDim = 2048) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width <= maxDim && height <= maxDim && file.size < 3_500_000) {
          resolve(file);
          return;
        }
        const scale = Math.min(maxDim / width, maxDim / height, 1);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/png");
      };
      img.src = URL.createObjectURL(file);
    });

  // --- Upload background ---
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const resized = await resizeImage(file);
      const form = new FormData();
      form.append("file", resized, file.name);
      const res = await fetch(`${API_URL}/scenes`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) throw new Error("Error al subir escenario");
      await loadScenes();
    } catch (err) {
      setErrorMsg(err.message);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // --- Import from Biblioteca ---
  const handleImportFromLibrary = async (img) => {
    setImporting(true);
    setShowLibrary(false);
    try {
      const res = await fetch(`${API_URL}/scenes/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ image_id: img.id }),
      });
      if (!res.ok) throw new Error("Error al importar");
      await loadScenes();
    } catch (err) {
      setErrorMsg(err.message);
    }
    setImporting(false);
  };

  // --- Delete background ---
  const handleDelete = async (path) => {
    try {
      await fetch(`${API_URL}/scenes/asset?path=${encodeURIComponent(path)}`, {
        method: "DELETE",
        headers,
      });
      if (selectedBg === path) setSelectedBg(null);
      await loadScenes();
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  // --- Generate variations ---
  const canGenerate = selectedBg && status !== "generating";

  const pollStatus = useCallback(async (jid) => {
    pollingRef.current = true;
    for (let i = 0; i < 120; i++) {
      if (!pollingRef.current) return;
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(
          `${API_URL}/scenes/${encodeURIComponent(jid)}/status`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        const data = await res.json();
        if (data.status === "completed") {
          const resultRes = await fetch(
            `${API_URL}/scenes/${encodeURIComponent(jid)}/result`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          const result = await resultRes.json();
          setGeneratedImage(result.image || null);
          setStatus("done");
          pollingRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollingRef.current = false;
          return;
        }
      } catch (err) {
        console.error("Poll error:", err);
      }
    }
    setErrorMsg("Tiempo agotado");
    setStatus("error");
    pollingRef.current = false;
  }, []);

  const handleGenerate = async () => {
    setStatus("generating");
    setGeneratedImage(null);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/scenes/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ background_path: selectedBg }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollStatus(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al enviar");
      setStatus("error");
    }
  };

  // --- Save ---
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!generatedImage) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/scenes/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ image_b64: generatedImage }),
      });
      if (res.ok) await loadScenes();
    } catch {}
    setSaving(false);
  };

  // --- Split ---
  const [splitting, setSplitting] = useState(false);
  const handleSplit = async () => {
    if (!generatedImage) return;
    setSplitting(true);
    try {
      const res = await fetch(`${API_URL}/scenes/split`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ image_b64: generatedImage }),
      });
      if (res.ok) await loadScenes();
    } catch {}
    setSplitting(false);
  };

  // --- Load variation into result ---
  const loadVariationToResult = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const b64 = reader.result.split(",")[1];
        setGeneratedImage(b64);
        setStatus("done");
      };
      reader.readAsDataURL(blob);
    } catch {}
  };

  const downloadImage = (b64) => {
    const link = document.createElement("a");
    link.href = b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
    link.download = "scene_variation.png";
    link.click();
  };

  const getB64Src = (b64) => {
    if (!b64) return null;
    return b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
  };

  return (
    <div className="sc-page">
      {/* LEFT — Scene management */}
      <div className="cs-panel cs-panel--left sc-panel--left">
        <div className="cs-result-header">
          <h2 className="cs-panel__title">Crea Escena</h2>
          <div className="sc-header-actions">
            <button
              className="sc-btn-upload"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Subiendo..." : "+ Subir escenario"}
            </button>
            <button
              className="sc-btn-library"
              onClick={() => setShowLibrary(true)}
              disabled={importing}
            >
              {importing ? "Importando..." : "Desde Biblioteca"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleUpload}
            />
            <button
              className={`cs-btn-generate ${status === "generating" ? "cs-btn-generate--loading" : ""}`}
              disabled={!canGenerate}
              onClick={handleGenerate}
            >
              {status === "generating" ? "Generando..." : "Generar Variaciones"}
            </button>
          </div>
        </div>

        {/* Backgrounds reel */}
        <section className="cs-section">
          <span className="cs-section__title">Escenarios</span>
          <div className="cs-reel cs-reel--tall">
            <button className="cs-reel__btn" onClick={() => scrollReel(bgsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={bgsReelRef}>
              {backgrounds.length > 0 ? (
                backgrounds.map((item) => {
                  const path = `scenes/backgrounds/${item.name}`;
                  const isSelected = selectedBg === path;
                  return (
                    <div
                      key={item.name}
                      className={`cs-reel-item cs-reel-item--wide sc-bg-item ${isSelected ? "sc-bg-item--selected" : ""}`}
                      onClick={() => { setSelectedBg(path); setGeneratedImage(null); setStatus("idle"); setErrorMsg(""); }}
                    >
                      <img src={item.url} alt={item.name} onDoubleClick={(e) => { e.stopPropagation(); setModalSrc(item.url); }} />
                      <button
                        className="sc-bg-item__delete"
                        onClick={(e) => { e.stopPropagation(); handleDelete(path); }}
                        title="Eliminar"
                      >&times;</button>
                      {isSelected && <span className="sc-bg-item__check">&#10003;</span>}
                      <span className="cs-reel-item__name">{item.name.replace(".png", "").replace(/_/g, " ")}</span>
                    </div>
                  );
                })
              ) : (
                <span className="cs-reel-empty">Sube un escenario o importa desde Biblioteca</span>
              )}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(bgsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Saved variations */}
        <section className="cs-section">
          <span className="cs-section__title">Variaciones generadas</span>
          <div className="cs-reel">
            <button className="cs-reel__btn" onClick={() => scrollReel(varsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={varsReelRef}>
              {variations.length > 0 ? (
                variations.map((item) => (
                  <div key={item.name} className="cs-reel-item cs-reel-item--sheet" onClick={() => loadVariationToResult(item.url)} title="Cargar en resultado">
                    <img src={item.url} alt={item.name} />
                  </div>
                ))
              ) : (
                <span className="cs-reel-empty">Genera variaciones de escenarios</span>
              )}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(varsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {errorMsg && <div className="cs-error">{errorMsg}</div>}
      </div>

      {/* RIGHT — Result */}
      <div className="cs-panel cs-panel--right sc-panel--right">
        <h2 className="cs-panel__title">Resultado</h2>

        {status === "idle" && !generatedImage && (
          <div className="cs-results-empty">
            <p>Selecciona un escenario y pulsa Generar Variaciones</p>
          </div>
        )}

        <div className="cs-results-grid">
          <div className="cs-result-card">
            <div
              className={`cs-result-card__image ${generatedImage ? "cs-result-card__image--loaded" : ""}`}
              onClick={() => generatedImage && setModalSrc(getB64Src(generatedImage))}
            >
              {generatedImage ? (
                <img src={getB64Src(generatedImage)} alt="Scene variations" />
              ) : (
                <div className="cs-result-card__placeholder">
                  {status === "generating" && <span className="cs-spinner" />}
                </div>
              )}
            </div>
            <div className="cs-result-card__footer">
              <span className="cs-result-card__label">Variaciones de escenario</span>
              <div className="cs-result-card__actions">
                {generatedImage && (
                  <button className="cs-btn-save" onClick={handleSave} disabled={saving}>
                    {saving ? "Guardando..." : "Guardar"}
                  </button>
                )}
                {generatedImage && (
                  <button className="sc-btn-split" onClick={handleSplit} disabled={splitting} title="Cortar en 4 variaciones individuales">
                    {splitting ? "Cortando..." : "Split"}
                  </button>
                )}
                {generatedImage && (
                  <button className="cs-result-card__download" onClick={() => downloadImage(generatedImage)}>
                    &#x2B73;
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalSrc && <ImageModal src={modalSrc} label="Escenario" onClose={() => setModalSrc(null)} />}
      {showLibrary && <LibraryPicker onSelect={handleImportFromLibrary} onClose={() => setShowLibrary(false)} />}
    </div>
  );
}
